/* eslint-disable react-hooks/exhaustive-deps */
import { Box, IconButton, Typography, Paper, Tooltip } from "@mui/material"
import { ChevronLeft, ChevronRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from "@mui/icons-material"
import { Bar } from "react-chartjs-2"
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from "chart.js"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend)

interface BarChartWithScrollProps<T> {
  data: T[]
  labelField: keyof T
  valueField: keyof T
  visibleItems?: number
  tooltipLabel?: string
  onItemClick?: (item: T, index: number) => void
  formatValue?: (value: any) => string
  barColor?: string
  selectedItemId?: string | number
  selectedBarColor?: string;
  scrollIndex: number
  setScrollIndex: (value: number) => void
}

export function BarChartWithScroll<T extends Record<string, any>>({
  data,
  labelField,
  valueField,
  visibleItems = 5,
  tooltipLabel = "Amount",
  onItemClick,
  formatValue = (val) => val?.toString() || "0",
  barColor = "var(--chart-1)",
  selectedItemId,
  selectedBarColor = "#147c9d",
  scrollIndex,
  setScrollIndex,
}: BarChartWithScrollProps<T>) {

  const blockSize = Math.max(visibleItems, 10)
  
  const visibleData = data.slice(scrollIndex, scrollIndex + visibleItems)

  const truncateLabel = (label: string, maxLength = 15) => {
    return label.length > maxLength ? label.substring(0, maxLength) + "..." : label
  }

  const getBarColor = (item: T, index: number) => {
    const actualIndex = scrollIndex + index
    const itemId = data[actualIndex]?.id
    if (selectedItemId !== undefined && itemId !== undefined && itemId === selectedItemId) {
      return selectedBarColor
    }
    
    return barColor
  }

  const chartData = {
    labels: visibleData.map((item) => truncateLabel(String(item[labelField]))),
    datasets: [
      {
        label: tooltipLabel,
        data: visibleData.map((item) => Number.parseFloat(item[valueField]) || 0),
        backgroundColor: visibleData.map((item, index) => getBarColor(item, index)),
        borderRadius: 4,
        borderSkipped: false,
        barPercentage: 0.5,
        categoryPercentage: 0.6,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event: any, elements: any) => {
      if (elements.length > 0 && onItemClick) {
        const index = elements[0].index
        const actualIndex = scrollIndex + index
        onItemClick(data[actualIndex], actualIndex)
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (context: any) => {
            const index = context[0].dataIndex
            const actualIndex = scrollIndex + index
            return String(data[actualIndex]?.[labelField] || "")
          },
          label: (context: any) => {
            return `${tooltipLabel}: ${formatValue(context.parsed.y)}`
          },
        },
      },
      datalabels: {
        display: true,
        color: "var(--foreground)",
        anchor: "end",
        align: "top",
        formatter: (value: number) => formatValue(value),
        font: {
          weight: "bold",
          size: 11,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
      y: {
        title: {
          display: false,
        },
        beginAtZero: true,
        grid: {
          display: true,
        },
        ticks: {
          display: true,
        },
      },
    },
    layout: {
      padding: {
        top: 20,
      },
    },
  }

  const canScrollLeft = scrollIndex > 0
  const canScrollRight = scrollIndex + visibleItems < data.length
  const canScrollBlockLeft = scrollIndex - blockSize >= 0
  const canScrollBlockRight = scrollIndex + blockSize < data.length

  const handleBlockLeft = () => {
    const newIndex = Math.max(0, scrollIndex - blockSize)
    setScrollIndex(newIndex)
  }

  const handleBlockRight = () => {
    const newIndex = Math.min(data.length - visibleItems, scrollIndex + blockSize)
    setScrollIndex(newIndex)
  }

  return (
    <Paper
      elevation={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "var(--card)",
        padding: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          position: "relative",
          minHeight: 0,
        }}
      >
        <Bar 
          data={chartData} 
          options={chartOptions}
          plugins={[{
            id: 'datalabels',
            afterDraw: (chart: any) => {
              const ctx = chart.ctx;
              ctx.save();
              
              chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
                const meta = chart.getDatasetMeta(datasetIndex);
                
                meta.data.forEach((bar: any, index: number) => {
                  const value = dataset.data[index];
                  const x = bar.x;
                  const y = bar.y;
                  if (value > 0) {
                    ctx.fillStyle = "var(--foreground)";
                    ctx.font = "bold 11px sans-serif";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "bottom";
                    const labelY = y - 8;
                    ctx.fillText(formatValue(value), x, labelY);
                  }
                });
              });
              
              ctx.restore();
            }
          }]}
        />
      </Box>

      {data.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            marginTop: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title={`Anterior bloque de ${Math.min(blockSize, data.length)}`}>
                <IconButton
                  size="small"
                  onClick={handleBlockLeft}
                  disabled={!canScrollBlockLeft}
                  sx={{
                    color: canScrollBlockLeft ? "var(--primary)" : "var(--muted-foreground)",
                  }}
                >
                  <KeyboardDoubleArrowLeft fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title="Anterior">
                <IconButton
                  size="small"
                  onClick={() => setScrollIndex(Math.max(0, scrollIndex - 1))}
                  disabled={!canScrollLeft}
                  sx={{
                    color: canScrollLeft ? "var(--primary)" : "var(--muted-foreground)",
                  }}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
              </Tooltip>

              <Typography variant="caption" sx={{ minWidth: "120px", textAlign: "center" }}>
                {scrollIndex + 1} - {Math.min(scrollIndex + visibleItems, data.length)} de {data.length}
              </Typography>

              <Tooltip title="Siguiente">
                <IconButton
                  size="small"
                  onClick={() => setScrollIndex(Math.min(data.length - visibleItems, scrollIndex + 1))}
                  disabled={!canScrollRight}
                  sx={{
                    color: canScrollRight ? "var(--primary)" : "var(--muted-foreground)",
                  }}
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title={`Siguiente bloque de ${Math.min(blockSize, data.length - scrollIndex)}`}>
                <IconButton
                  size="small"
                  onClick={handleBlockRight}
                  disabled={!canScrollBlockRight}
                  sx={{
                    color: canScrollBlockRight ? "var(--primary)" : "var(--muted-foreground)",
                  }}
                >
                  <KeyboardDoubleArrowRight fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  )
}